import { useEffect, useState } from 'react';
import { useFaceApi } from './';
import { calibrationConstants } from '../calibration-constants';

let testContent = {
  changeCounter: 0,
  strangeMovementCounter: 0,
  isMoving: false,
  baseWidth: 0,
  temporaryWidth: 0,
  attempts: 0,
  sourceContentSize: 0,
  sourceLandmarkData: 0,
  baseDistance: 0,
};

// REFACTOR NEEDED
const useDistanceDetector = (activate) => {
  const [calculatedDistance, setCalculatedDistance] = useState(0);
  const faceApiDetections = useFaceApi(activate);
  const [detectionMethod, setDetectionMethod] = useState('pupillaryDistance');
  const [movementNotification, setMovementNotification] = useState(null);
  const [readyToStart, setReadyToStart] = useState(false);
  const [perceivedFocalLength, setPerceivedFocalLength] = useState(0);

  const getLandmarkData = (landmarks) => {
    switch (detectionMethod) {
      case 'faceWidth':
        return getFaceWidth(landmarks);
      case 'pupillaryDistance':
        return getPupillaryDistance(landmarks);
      default:
        return;
    }
  };

  const calculateDistance = (landmarkData) => {
    switch (detectionMethod) {
      case 'faceWidth':
        return (calibrationConstants.averageAdultHumanFaceWidthMm * perceivedFocalLength) / landmarkData;
      case 'pupillaryDistance':
        return (calibrationConstants.averagePupillaryDistanceMm * perceivedFocalLength) / landmarkData;
      default:
        return (calibrationConstants.averagePupillaryDistanceMm * perceivedFocalLength) / landmarkData;
    }
  };

  const calculatedPerceivedFocalLength = (landmarkWidth, baseDistance) => {
    switch (detectionMethod) {
      case 'faceWidth':
        return Math.round((landmarkWidth * baseDistance) / calibrationConstants.averageAdultHumanFaceWidthMm);
      case 'pupillaryDistance':
        return Math.round((landmarkWidth * baseDistance) / calibrationConstants.averagePupillaryDistanceMm);
      default:
        return Math.round((landmarkWidth * baseDistance) / calibrationConstants.averagePupillaryDistanceMm);
    }
  };

  const getEyePupilPosition = (landmarks, eye) => {
    let eyeLandmarks = landmarks.getLeftEye();
    if (eye === 'right') {
      eyeLandmarks = landmarks.getRightEye();
    }

    const minEyeXPosition = eyeLandmarks.reduce(function (currentValue, initialValue) {
      return currentValue._x < initialValue._x ? currentValue : initialValue;
    });

    const maxEyeXPosition = eyeLandmarks.reduce(function (currentValue, initialValue) {
      return currentValue._x > initialValue._x ? currentValue : initialValue;
    });

    const eyeWidth = maxEyeXPosition._x - minEyeXPosition._x;

    return minEyeXPosition._x + eyeWidth / 2;
  };

  const getPupillaryDistance = (landmarks) => {
    const leftEyePupilPosition = getEyePupilPosition(landmarks, 'left');
    const rightEyePupilPosition = getEyePupilPosition(landmarks, 'right');
    return Math.round(rightEyePupilPosition - leftEyePupilPosition);
  };

  const getFaceWidth = (landmarks) => {
    return landmarks.imageWidth;
  };

  const checkAndProcessChanges = (landmarks) => {
    const landmarkData = getLandmarkData(landmarks);
    if (testContent.baseDistance && !testContent.baseWidth) {
      testContent.baseWidth = landmarkData;
      return setPerceivedFocalLength(calculatedPerceivedFocalLength(landmarkData, testContent.baseDistance));
    }
    const baseWidthInterval = testContent.baseWidth - landmarkData;
    const temporaryWidthInterval = testContent.temporaryWidth - landmarkData;
    if (baseWidthInterval >= 25 || baseWidthInterval <= -25) {
      if (testContent.attempts > 0) {
        testContent.isMoving = true;
        setMovementNotification({
          patientDistance: testContent.baseDistance,
          patientIsMoving: testContent.isMoving,
          counter: 4 - testContent.changeCounter,
        });
        testContent.temporaryWidth = landmarkData;
      } else {
        testContent.attempts = 1;
        return;
      }

      if (temporaryWidthInterval >= 25 || temporaryWidthInterval <= -25) {
        testContent.changeCounter = 0;
        testContent.strangeMovementCounter = 1;
      } else {
        if (testContent.changeCounter > 3) {
          const newDistance = calculateDistance(landmarkData);

          setCalculatedDistance(newDistance);

          testContent.baseWidth = landmarkData;
          testContent.baseDistance = newDistance;
          testContent.isMoving = false;

          setMovementNotification({
            patientDistance: Math.round(newDistance),
            patientIsMoving: testContent.isMoving,
            counter: 4 - testContent.changeCounter,
          });

          testContent.changeCounter = 0;
          testContent.strangeMovementCounter = 1;
          testContent.attempts = 0;
        } else {
          testContent.changeCounter++;
        }
      }
    } else {
      if (testContent.attempts > 0) {
        testContent.attempts = 0;
      }
      if (testContent.isMoving === true) {
        testContent.changeCounter = 0;
        if (testContent.strangeMovementCounter > 3) {
          testContent.isMoving = false;
          setMovementNotification({
            patientDistance: testContent.baseDistance,
            patientIsMoving: testContent.isMoving,
            counter: 4 - testContent.strangeMovementCounter,
          });
          testContent.strangeMovementCounter = 1;
          testContent.attempts = 0;
        } else {
          testContent.isMoving = true;
          setMovementNotification({
            patientDistance: testContent.baseDistance,
            patientIsMoving: testContent.isMoving,
            counter: 4 - testContent.strangeMovementCounter,
          });
          testContent.strangeMovementCounter++;
        }
      }
    }
  };

  const start = (baseDistance, isMoving, calculatedPerceivedFocalLength) => {
    testContent.baseDistance = baseDistance;
    testContent.isMoving = isMoving;
    testContent.baseWidth = 0;
    testContent.changeCounter = 0;
    testContent.strangeMovementCounter = 0;
    testContent.temporaryWidth = 0;
    if (calculatedPerceivedFocalLength) {
      setPerceivedFocalLength(calculatedPerceivedFocalLength);
      // we check if the perceived focal length was already calculated during
      // the resizing operations. If that is the case, set the base distance to
      // 0 and grab the new distance.
    } else if (perceivedFocalLength !== 0) {
      testContent.baseDistance = 0;
    }
    setMovementNotification({
      patientDistance: baseDistance,
      patientIsMoving: isMoving,
      counter: 4,
    });
    setReadyToStart(true);
  };

  const changeDistanceDetectionMethod = (newDetectionMethod) => {
    setDetectionMethod(newDetectionMethod);
  };

  useEffect(() => {
    if (faceApiDetections && faceApiDetections.landmarks && readyToStart === true) {
      checkAndProcessChanges(faceApiDetections.landmarks);
    }
  }, [faceApiDetections, readyToStart]);

  return {
    calculatedDistance,
    movementNotification,
    start,
    changeDistanceDetectionMethod,
    setReadyToStart,
    faceApiDetections,
    getLandmarkData,
    calculatedPerceivedFocalLength,
  };
};

export default useDistanceDetector;
